import { ROUTE_AVAILABILITY_DETAILS } from '@/constants/routes';
import useAuth from '@/hooks/useAuth';
import { useListOrganizations } from '@/queries/organizations.queries';
import {
	useCreateResourceMutation,
	useListResourcesQuery,
	useRemoveResourceMutation,
} from '@/queries/resources.queries';
import uuidService from '@/services/uuid.service';
import { IOrganization } from '@/types/IOrganization';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

export function useAvailabilityList() {
	const toast = useToast();
	const auth = useAuth();
	const orgsQuery = useListOrganizations();
	const query = useListResourcesQuery({ enabled: !!orgsQuery.data });
	const { t } = useTranslation();
	const createResourceMutation = useCreateResourceMutation();
	const removeResourceMutation = useRemoveResourceMutation();

	const history = useHistory();

	function createResource() {
		const id = uuidService.v4();

		return createResourceMutation
			.mutateAsync({
				id,
				bookingRules: {
					/**
					 * Since this resource represents a user
					 * we only allow one booking at a time.
					 */
					maxConcurrentBookings: 1,
					repeatingAvailability: null,
					checkAvailabilityInCalendars: [],
				},
				name: auth.getCurrentUser().slug,
				tags:
					query.data?.resources.filter((resource) => resource.owner.id === auth.getCurrentUser().userId).length === 0
						? { tt_default: 'true' }
						: {},
				collaborators:
					orgsQuery.data?.organizations.map((org: IOrganization) => ({
						type: 'ORGANIZATION',
						organizationId: org.id,
					})) || [],
			})
			.then(() => {
				history.push(ROUTE_AVAILABILITY_DETAILS.replace(':id', id));
			})
			.catch(() => {});
	}

	function handleDeleteClick(id: string) {
		if (!window.confirm('')) {
			return;
		}
		return removeResourceMutation.mutateAsync({ id }).then(() => {
			toast({
				duration: 3000,
				isClosable: true,
				status: 'success',
				title: t('Resource deleted'),
			});
		});
	}

	function handleDefaultClick(id: string) {
		query.data?.resources
			// Filter owned resources
			.filter((resource) => resource.owner.id === auth.getCurrentUser().userId)
			.forEach((resource) => {
				if (resource.id === id) {
					// Add the tt_default property to tags object
					resource.tags = {
						tt_default: 'true',
					};
					createResourceMutation.mutateAsync(resource);
				}
				if (resource.id !== id) {
					// Delete the tt_default property from tags object
					delete resource.tags?.tt_default;
					createResourceMutation.mutateAsync(resource);
				}
			});
	}

	return {
		handleDefaultClick,
		handleDeleteClick,
		createResource,
		auth,
		history,
		query,
		t,
	};
}
